/**
 * The pure parts of the themed dropdown (components/ui/Select.tsx), kept apart so unit tests can
 * pin them: moving the highlight past disabled options, type-to-jump, the search box's filter, and
 * where the option list opens (under the field, or above it when there is more room there).
 */

import { matchesSearch } from "./clientList";

/** What the keyboard logic needs to know about an option. */
export interface ListItem {
  label: string;
  hint?: string;
  /** Searched but not shown, e.g. a state's code. */
  keywords?: string;
  disabled?: boolean;
}

/**
 * Where the highlight goes from `from` by `step` (±1 for the arrow keys, ±10 for Page Up/Down):
 * disabled options are skipped and it stops at either end instead of wrapping round. From -1
 * (nothing highlighted) +1 lands on the first enabled option and -1 on the last. -1 when no option
 * is enabled.
 */
export function moveHighlight(items: readonly ListItem[], from: number, step: number): number {
  const n = items.length;
  if (n === 0 || step === 0) return -1;
  const dir = step > 0 ? 1 : -1;
  const start = from < 0 || from >= n ? (dir > 0 ? -1 : n) : from;
  const target = Math.min(n - 1, Math.max(0, start + step));
  for (let i = target; i >= 0 && i < n; i += dir) if (!items[i].disabled) return i;
  // Nothing enabled further on: the nearest enabled one back towards where it started.
  for (let i = target - dir; i >= 0 && i < n; i -= dir) if (!items[i].disabled) return i;
  return -1;
}

/** Lower case, accents dropped, so "É" is typed as "e". */
const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Type-to-jump: the enabled option whose label starts with what was typed. A single letter, or the
 * same letter typed again and again, steps to the next option starting with it after `from`,
 * wrapping round; a longer string tries the highlighted option itself first, so typing on keeps
 * the highlight while it still fits. -1 when no label starts with it.
 */
export function typeahead(items: readonly ListItem[], typed: string, from: number): number {
  const text = fold(typed);
  const n = items.length;
  if (!text || n === 0) return -1;
  const sameLetter = [...text].every((c) => c === text[0]);
  const needle = sameLetter ? text[0] : text;
  const first = sameLetter ? from + 1 : Math.max(from, 0);
  for (let k = 0; k < n; k++) {
    const i = (((first + k) % n) + n) % n;
    if (!items[i].disabled && fold(items[i].label).startsWith(needle)) return i;
  }
  return -1;
}

/** The options a search box keeps: every word of the query in the label, hint or keywords, as table searches match. */
export function searchOptions<O extends ListItem>(items: readonly O[], query: string): readonly O[] {
  return query.trim() ? items.filter((o) => matchesSearch(`${o.label} ${o.hint ?? ""} ${o.keywords ?? ""}`, query)) : items;
}

/**
 * Where the list goes, in viewport pixels. Always a `top` (a list above its field is placed by its
 * own height), since a `bottom` would be measured from the layout viewport, which on a phone runs
 * on under the on-screen keyboard.
 */
export interface ListPlacement {
  side: "below" | "above";
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Places the option list (`height` tall unclamped) against its field (`anchor`, a viewport
 * rectangle): under it when the whole list fits there or there is at least as much room as above,
 * otherwise above it. As wide as the field but at least `minWidth`, shifted to stay `margin` inside
 * the screen's sides; no taller than `maxHeight` or the room on its side, but never squeezed below
 * `minHeight`, so a field at the screen's edge still shows a few options. `viewport.height` is the
 * visible part (above a phone's keyboard). `keep` holds an open list on the side it opened on
 * (searching shrinks it; it shouldn't jump across its field) while that side has room for it.
 */
export function placeList(
  anchor: { top: number; bottom: number; left: number; width: number },
  viewport: { width: number; height: number },
  { height, minWidth = 0, maxHeight = 320, minHeight = 120, gap = 4, margin = 8, keep }: {
    height: number;
    minWidth?: number;
    maxHeight?: number;
    minHeight?: number;
    gap?: number;
    margin?: number;
    keep?: "below" | "above";
  },
): ListPlacement {
  const width = Math.max(0, Math.min(Math.max(anchor.width, minWidth), viewport.width - 2 * margin));
  const left = Math.max(margin, Math.min(anchor.left, viewport.width - margin - width));
  const below = viewport.height - anchor.bottom - gap - margin;
  const above = anchor.top - gap - margin;
  const wanted = Math.min(height, maxHeight);
  const kept = keep && (keep === "below" ? below : above) >= Math.min(wanted, minHeight) ? keep : null;
  const side = kept ?? (below >= wanted || below >= above ? "below" : "above");
  const room = side === "below" ? below : above;
  const fitted = Math.max(Math.min(maxHeight, room), Math.min(minHeight, maxHeight));
  const top = side === "below" ? anchor.bottom + gap : anchor.top - gap - Math.min(height, fitted);
  return { side, top, left, width, maxHeight: fitted };
}
